'use client'

import React from 'react'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

// ============================================================================
// Types
// ============================================================================

interface ChartDataSeries {
  name: string
  data: number[]
  color?: string
}

interface ChartData {
  xAxis?: string[]
  series: ChartDataSeries[]
}

interface LineChartWidgetProps {
  data: ChartData
  title?: string
  type?: 'LineChart' | 'BarChart'
}

// ============================================================================
// Color Palette
// ============================================================================

const DEFAULT_COLORS = [
  '#6366f1', // indigo
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
]

// ============================================================================
// Line Chart Widget
// ============================================================================

export function LineChartWidget({ data, title, type = 'LineChart' }: LineChartWidgetProps) {
  // Transform data to Recharts format
  const transformedData = transformDataForRecharts(data)

  if (transformedData.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        {title && <h3 className="font-semibold text-brand-navy mb-2">{title}</h3>}
        <p className="text-sm text-gray-500">No data available</p>
      </div>
    )
  }

  const Chart = type === 'BarChart' ? BarChart : LineChart
  const DataComponent = type === 'BarChart' ? Bar : Line

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      {title && <h3 className="font-semibold text-brand-navy mb-3">{title}</h3>}
      
      <ResponsiveContainer width="100%" height={250}>
        <Chart data={transformedData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="x" 
            tick={{ fontSize: 12 }}
            stroke="#6b7280"
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            stroke="#6b7280"
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              fontSize: '12px'
            }}
          />
          <Legend 
            wrapperStyle={{ fontSize: '12px' }}
          />
          {data.series.map((series, idx) => (
            <DataComponent
              key={series.name}
              type="monotone"
              dataKey={series.name}
              stroke={series.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
              fill={series.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
              strokeWidth={2}
            />
          ))}
        </Chart>
      </ResponsiveContainer>
    </div>
  )
}

// ============================================================================
// Helper Functions
// ============================================================================

function transformDataForRecharts(data: ChartData): Array<Record<string, string | number>> {
  const { xAxis, series } = data

  if (!xAxis || xAxis.length === 0) {
    return []
  }

  // Create array of objects where each object has x and values for each series
  return xAxis.map((x, index) => {
    const point: Record<string, string | number> = { x }
    
    series.forEach(s => {
      if (s.data && s.data[index] !== undefined) {
        point[s.name] = s.data[index]
      }
    })
    
    return point
  })
}
